import React, { useCallback, useContext, useMemo } from 'react';
import { CustomTooltipComponent } from './SchedulerComponent/Components/CustomTooltipComponent';
import {
  IBookingType,
  IResource,
} from './SchedulerComponent/Components/AppointmentComponent';
import { IBooking } from '../../../../../../BaseComponents/CalendarView/Utils/EDCalendarViewUtils';
import format from 'date-fns/format';
import { tryDateTimeStringToDateTime } from '../../../../../../Utils/dateUtils';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { useQuery } from '@apollo/client/react';
import {
  MeQuery,
  MeQueryVariables,
} from '../Queries/MeQuery/_QueryTypes/MeQuery';
import meQuery from '../Queries/MeQuery/MeQuery.gql';
import EDPopover from '../../../../../../BaseComponents/Popover/EDPopover';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import { CustomTooltipHeaderComponent } from './SchedulerComponent/Components/CustomToolipHeaderComponent';
import { AppointmentTooltipContextProvider } from './AppointmentTooltipContext';

interface IProps {
  open: boolean;
  onClose: () => void;
  onSelectDetail: (id: TSelectedDetail, force?: boolean) => void;
  anchorEl: HTMLElement | null;
  booking: IBooking;
}

const convertBookingToBookingType = (booking: IBooking): IBookingType => {
  return {
    startDate: format(booking.startTime, 'yyyy-MM-dd HH:mm'),
    endDate: format(booking.endTime, 'yyyy-MM-dd HH:mm'),
    title: booking.aktCaption ?? '-',
    allDay: false,
    bookingId: booking.bookingId ?? '-',
    bookingNumber: '-',
    bokningssignatur: booking.aktBokningssignatur,
    aktStartDate: format(
      booking.aktStartTime || booking.startTime,
      'yyyy-MM-dd HH:mm'
    ),
    aktEndDate: format(
      booking.aktEndTime || booking.endTime,
      'yyyy-MM-dd HH:mm'
    ),
    aktivityType: booking.aktTyp,
    resourceName: booking.resourceName,
    activityId: booking.aktId,
    activity: {
      id: booking.aktId,
      _typ: booking.aktTyp,
    },
    resourceNameList: [],
    bokningsbar: false,
    location: { resName: booking.aktLocation } as IResource,
    isPerson: booking.isPerson,
  };
};

/**
 * Formattera datum på samma sätt som i den gamla kalenderkomponenten
 * Funktionen ska hantera de alternativ som används för popup-komponenten
 * När dx-react-scheduler inte används mer bör funktionen tas bort
 * */
const compatFormatDate = (
  date: Date | number | string,
  options: {
    year?: string;
    month?: string;
    day?: string;
    weekday?: string;
    hour?: string;
    minute?: string;
  }
) => {
  const dateObj: Date | undefined =
    typeof date === 'string'
      ? tryDateTimeStringToDateTime(date.replace(/\D/g, ''))
      : typeof date === 'number'
      ? new Date(date)
      : date;
  const yearFormat = options.year ? 'yyyy' : '';
  const monthFormat =
    options.month === 'long' ? 'MMMM' : options.month === 'numeric' ? 'MM' : '';
  const dayFormat =
    options.day === 'long' ? 'dd' : options.day === 'numeric' ? 'd' : '';
  const weekDayFormat =
    options.weekday === 'long'
      ? 'EEEE'
      : options.weekday === 'short'
      ? 'EEE'
      : '';
  const dayMonthSeparator = dayFormat && monthFormat ? '-' : ''; //Lägger till - bara när både dag och månad
  const dateTimeSeparator =
    (options.year || options.month || options.day) &&
    (options.hour || options.minute)
      ? ' '
      : '';
  const yearSeparator = (dayFormat || monthFormat) && yearFormat ? ' ' : ''; //Lägger till space bara när dag/månad och år
  const hourFormat = options.hour ? `HH${options.minute ? ':' : ''}` : '';
  const minuteFormat = options.minute ? 'mm' : '';
  const longFormat = !!options.weekday; //om veckodag ska visas byter vi ordning på formatet
  const formatStr = longFormat
    ? `${weekDayFormat} ${dayFormat} ${monthFormat} ${yearFormat} ${hourFormat}${minuteFormat}`
    : `${dayFormat}${dayMonthSeparator}${monthFormat}${yearSeparator}${yearFormat}${dateTimeSeparator}${hourFormat}${minuteFormat}`;
  return dateObj ? format(dateObj, formatStr) : '';
};

const POPUP_WIDTH = 400;

const useHorizontalOrigin = (
  anchorEl: HTMLElement | null
): 'left' | 'center' => {
  if (!anchorEl) return 'left';
  const rect = anchorEl.getBoundingClientRect();
  const spaceOnRight = window.innerWidth - rect.left;
  return spaceOnRight >= POPUP_WIDTH ? 'left' : 'center';
};

export const KalenderBookingPopup: React.FC<IProps> = props => {
  const databaseContext = useContext(ActiveDatabaseContext);
  const availableRoutines = databaseContext.availableRoutines;
  const appointmentData = convertBookingToBookingType(props.booking);
  const isSmallScreen = useIsSmallScreen();
  const horizontalOrigin = useHorizontalOrigin(props.anchorEl);
  const onOpenButtonClick = useCallback(() => {
    props.onSelectDetail({
      id: appointmentData.activityId ?? '',
      data: appointmentData.activity,
    });
    props.onClose();
  }, [appointmentData.activityId, appointmentData.activity, props]);

  const actionRef = React.useRef(null);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const activeDatabaseOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabase ?? 'noDBYet',
      },
      skip: !activeDatabase,
    }),
    [activeDatabase]
  );
  const { data: me_data } = useQuery<MeQuery, MeQueryVariables>(
    meQuery,
    activeDatabaseOptions
  );
  const canEditBooking =
    me_data?.me?.[0]?.userCanEditBooking ||
    appointmentData.bokningssignatur === me_data?.me?.[0]?.sign;

  return (
    <AppointmentTooltipContextProvider actions={actionRef}>
      <EDPopover
        open={props.open}
        onClose={props.onClose}
        anchorEl={props.anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: horizontalOrigin }}
        style={{
          margin: 0,
          padding: '6px',
          maxWidth: isSmallScreen ? undefined : '50%',
          maxHeight: '80%',
          width: '400px',
        }}
        action={actionRef}
      >
        <CustomTooltipHeaderComponent
          showOpenButton
          showCloseButton={false}
          showDeleteButton={false}
          canEditBooking={canEditBooking}
          availableRoutines={availableRoutines}
          onOpenButtonClick={onOpenButtonClick}
          onClose={props.onClose}
          data={appointmentData}
        />
        <CustomTooltipComponent
          appointmentData={appointmentData}
          formatDate={compatFormatDate}
          recurringIconComponent={() => <p>recurring</p>}
        ></CustomTooltipComponent>
      </EDPopover>
    </AppointmentTooltipContextProvider>
  );
};
